import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadBlob, downloadCsv, toCsv } from '../../../utils/export';
import { httpClient } from '../../../api/http-client';
import { browserLogger } from '../../../runtime/logging/browser-logger';
import { useNotify } from '../../../features/notifications/notification-store';
import { resolveTKey } from '../../../i18n/resolve-key';
import type { TableRendererProps } from './table-renderer-props.ts';
import type { UseTableDataReturn } from './use-table-data.ts';
import { buildLocalTableExport } from './table-export-policy.js';

interface UseTableExportOptions {
  contract: TableRendererProps['contract'];
  tableData: UseTableDataReturn['data'];
  state: UseTableDataReturn['state'];
  getRowIdValue: (row: Record<string, unknown>) => string | undefined;
  onExport?: TableRendererProps['onExport'];
}

export function useTableExport({
  contract,
  tableData,
  state,
  getRowIdValue,
  onExport,
}: UseTableExportOptions): (options: {
  scope: 'page' | 'selection' | 'all';
  format: 'csv' | 'xlsx';
  fields?: string[];
}) => void {
  const { t } = useTranslation();
  const { notify } = useNotify();

  return useCallback(
    (options: { scope: 'page' | 'selection' | 'all'; format: 'csv' | 'xlsx'; fields?: string[] }) => {
      if (onExport) {
        onExport(options);
        return;
      }

      const unavailable = () =>
        notify(t(resolveTKey('tableExport.unavailable')), 'warning');
      const completed = () => notify(t(resolveTKey('tableExport.completed')), 'success');
      const failed = () => notify(t(resolveTKey('tableExport.failed')), 'error', 7000);

      const serverExport = contract.export?.serverExport;
      if (options.scope === 'all') {
        if (!serverExport) {
          browserLogger.warn(
            'table.export.server_required',
            'A backend export handler is required for all matching records.',
            { tableId: String(contract.id), format: options.format, scope: options.scope },
          );
          unavailable();
          return;
        }
        void (async () => {
          const params = new URLSearchParams();
          if (options.fields && options.fields.length > 0) {
            params.set('fields', options.fields.join(','));
          }
          if (options.format === 'xlsx') {
            params.set('format', 'xlsx');
          }
          if (serverExport.searchParameter && state.globalSearch.trim()) {
            params.set(serverExport.searchParameter, state.globalSearch.trim());
          }
          if (serverExport.filtersParameter && state.filters.length > 0) {
            params.set(
              serverExport.filtersParameter,
              JSON.stringify(
                state.filters.map((filter) => ({
                  field: filter.field,
                  operator: filter.operator,
                  value: filter.value,
                })),
              ),
            );
          }
          for (const filter of state.filters) {
            const parameter = serverExport.filterParameterMap?.[filter.field];
            if (
              parameter &&
              filter.value !== undefined &&
              filter.value !== null &&
              filter.value !== ''
            ) {
              params.set(parameter, String(filter.value));
            }
          }
          const activeSort = state.sorting[0];
          if (activeSort && serverExport.sortFieldParameter) {
            params.set(serverExport.sortFieldParameter, activeSort.field);
          }
          if (activeSort && serverExport.sortDirectionParameter) {
            params.set(serverExport.sortDirectionParameter, activeSort.direction);
          }

          const response = await httpClient(
            `${serverExport.endpoint}${params.size ? `?${params.toString()}` : ''}`,
            { method: serverExport.method },
          );
          if (!response.ok) {
            throw new Error(`SERVER_EXPORT_FAILED:${response.status}`);
          }
          const ext = options.format === 'xlsx' ? 'xlsx' : 'csv';
          const filename = `${String(contract.id)}-all.${ext}`;
          if (options.format === 'xlsx') {
            downloadBlob(filename, await response.blob());
          } else {
            downloadCsv(filename, await response.text());
          }
          completed();
        })().catch((error: unknown) => {
          browserLogger.error('table.export.server_failed', 'The server-owned export failed.', {
            tableId: String(contract.id),
            error: error instanceof Error ? error.message : String(error),
          });
          failed();
        });
        return;
      }

      if (options.format !== 'csv') {
        browserLogger.warn(
          'table.export.server_required',
          'A backend export handler is required for this format.',
          { tableId: String(contract.id), format: options.format, scope: options.scope },
        );
        unavailable();
        return;
      }

      const result = buildLocalTableExport(
        contract,
        tableData,
        state,
        getRowIdValue,
        options.scope,
        options.fields,
      );
      if (!result.allowed) {
        browserLogger.warn(
          'table.export.server_required',
          'A backend export handler is required by the resolved export policy.',
          {
            tableId: String(contract.id),
            format: options.format,
            scope: options.scope,
            reason: result.reason,
          },
        );
        unavailable();
        return;
      }

      const filename = `${String(contract.id)}-${options.scope}-${Date.now()}.csv`;
      downloadCsv(
        filename,
        toCsv(result.rows, {
          formulaInjectionProtection: contract.export?.formulaInjectionProtection !== false,
          headers: result.headers,
        }),
      );
      completed();
    },
    [onExport, contract, tableData, state, getRowIdValue, notify, t],
  );
}
